<template>
    <tr class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group hover:tw-bg-gray-50/50">
        <!-- ID -->
        <td class="tw-px-4 tw-py-3">
            <span class="tw-text-sm tw-font-medium tw-text-gray-900">#{{ item.id }}</span>
        </td>

        <!-- Name -->
        <td class="tw-px-4 tw-py-3">
            <div class="tw-flex tw-items-center tw-gap-3">
                <div v-if="item.logo" class="tw-w-8 tw-h-8 tw-rounded-full tw-overflow-hidden tw-bg-gray-100">
                    <img :src="item.logo" :alt="item.name" class="tw-w-full tw-h-full tw-object-cover" />
                </div>
                <div v-else class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
                    <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
                </div>
                <div>
                    <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{{ item.name }}</p>
                    <p v-if="item.website" class="tw-text-xs tw-text-gray-500 tw-truncate">
                        <a :href="item.website" target="_blank" class="tw-flex tw-items-center tw-gap-1 hover:tw-underline tw-truncate tw-max-w-[150px]">
                            {{ formatWebsite(item.website) }}
                            <icon icon="solar:link-bold-duotone" class="tw-text-gray-400 tw-text-xs" />
                        </a>
                    </p>
                </div>
            </div>
        </td>

        <!-- Primary Email -->
        <td class="tw-px-4 tw-py-3">
            <div 
                v-if="item.primary_email"
                @click="handleCopy(item.primary_email, 'email')"
                class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-group/email hover:tw-bg-gray-100 tw-rounded tw-px-2 tw-py-1 tw-transition-colors tw-duration-150"

                role="button"
                tabindex="0"
                @keydown.enter="handleCopy(item.primary_email, 'email')"
                @keydown.space.prevent="handleCopy(item.primary_email, 'email')"
            >
                <icon icon="solar:letter-bold-duotone" class="tw-text-gray-400 tw-text-sm" />
                <span class="tw-text-sm tw-text-gray-900 tw-truncate" :title="item.primary_email">
                    {{ copyFeedback.email ? 'Copied!' : item.primary_email }}
                </span>
                <icon 
                    v-if="false"
                    icon="solar:copy-bold-duotone" 
                    class="tw-text-gray-400 tw-text-sm tw-opacity-0 group-hover/email:tw-opacity-100 tw-transition-opacity tw-duration-150" 
                />
            </div>
            <div v-else class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:letter-bold-duotone" class="tw-text-gray-400 tw-text-sm" />
                <span class="tw-text-sm tw-text-gray-500">-</span>
            </div>
        </td>

        <!-- Primary Phone -->
        <td class="tw-px-4 tw-py-3">
            <div 
                v-if="item.primary_phone"
                @click="handleCopy(item.primary_phone, 'phone')"
                class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-group/phone hover:tw-bg-gray-100 tw-rounded tw-px-2 tw-py-1 tw-transition-colors tw-duration-150"

                role="button"
                tabindex="0"
                @keydown.enter="handleCopy(item.primary_phone, 'phone')"
                @keydown.space.prevent="handleCopy(item.primary_phone, 'phone')"
            >
                <icon icon="solar:phone-bold-duotone" class="tw-text-gray-400 tw-text-sm" />
                <span class="tw-text-sm tw-text-gray-900">
                    {{ copyFeedback.phone ? 'Copied!' : item.primary_phone }}
                </span>
                <icon v-if="false"
                    icon="solar:copy-bold-duotone" 
                    class="tw-text-gray-400 tw-text-sm tw-opacity-0 group-hover/phone:tw-opacity-100 tw-transition-opacity tw-duration-150" 
                />
            </div>
            <div v-else class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:phone-bold-duotone" class="tw-text-gray-400 tw-text-sm" />
                <span class="tw-text-sm tw-text-gray-500">-</span>
            </div>
        </td>

        <!-- Country -->
        <td class="tw-px-4 tw-py-3">
            <div v-if="item.country" class="tw-flex tw-items-center tw-gap-2">
                <span class="tw-text-sm">{{ getCountryFlag(item.country) }}</span>
                <span class="tw-text-sm tw-text-gray-900">{{ item.country }}</span>
            </div>
            <span v-else class="tw-text-sm tw-text-gray-500">-</span>
        </td>

        <!-- Categories -->
        <td class="tw-px-4 tw-py-3">
            <div v-if="item.categories && item.categories.items && item.categories.items.length > 0" class="tw-flex tw-flex-nowrap tw-gap-2 tw-w-full">
                <span 
                    class="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-200 hover:tw-bg-blue-100 tw-transition-colors tw-duration-150 tw-min-w-0 tw-flex-1"
                    :title="item.categories.items[0].name"
                >
                    <icon icon="solar:widget-bold-duotone" class="tw-text-blue-600 tw-text-xs" />
                    <span class="tw-truncate">{{ item.categories.items[0].name }}</span>
                </span>
                <span 
                    v-if="item.categories.count > 1" 
                    class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-bg-gray-50 tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-100 tw-transition-colors tw-duration-150 tw-flex-shrink-0"
                    :title="getRemainingCategoriesText(item.categories.items.slice(1))"
                >
                    +{{ item.categories.count - 1 }}
                </span>
            </div>
            <span v-else class="tw-text-sm tw-text-gray-400">-</span>
        </td>

        <!-- Certificates -->
        <td class="tw-px-4 tw-py-3">
            <div v-if="item.certificates && item.certificates.items && item.certificates.items.length > 0" class="tw-flex tw-flex-nowrap tw-gap-2 tw-w-full">
                <span 
                    class="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-200 hover:tw-bg-emerald-100 tw-transition-colors tw-duration-150 tw-min-w-0 tw-flex-1"
                    :title="item.certificates.items[0].name"
                >
                    <icon icon="solar:medal-ribbons-star-bold" class="tw-text-emerald-600 tw-text-xs" />
                    <span class="tw-truncate">{{ item.certificates.items[0].name }}</span>
                </span>
                <span 
                    v-if="item.certificates.count > 1" 
                    class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium tw-bg-gray-50 tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-100 tw-transition-colors tw-duration-150 tw-flex-shrink-0"
                    :title="getRemainingCertificatesText(item.certificates.items.slice(1))"
                >
                    +{{ item.certificates.count - 1 }}
                </span>
            </div>
            <span v-else class="tw-text-sm tw-text-gray-400">-</span>
        </td>

        <!-- Users Count -->
        <td class="tw-px-4 tw-py-3">
            <div class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-gray-400 tw-text-sm" />
                <span class="tw-text-sm tw-font-medium tw-text-gray-900">{{ item.users_count || 0 }}</span>
            </div>
        </td>

        <!-- Social Media -->
        <td class="tw-px-4 tw-py-3">
            <div v-if="hasSocialMedia(item)" class="tw-flex tw-items-center tw-gap-1">
                <a 
                    v-if="item.facebook" 
                    :href="item.facebook" 
                    target="_blank" 
                    class="tw-p-1 tw-rounded tw-text-gray-400 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-colors tw-duration-150"
                    :title="`Facebook: ${item.facebook}`"
                >
                    <icon icon="ri:facebook-fill" class="tw-text-sm" />
                </a>
                <a 
                    v-if="item.twitter" 
                    :href="item.twitter" 
                    target="_blank" 
                    class="tw-p-1 tw-rounded tw-text-gray-400 hover:tw-text-blue-400 hover:tw-bg-blue-50 tw-transition-colors tw-duration-150"
                    :title="`Twitter: ${item.twitter}`"
                >
                    <icon icon="hugeicons:new-twitter" class="tw-text-sm" />
                </a>
                <a 
                    v-if="item.instagram" 
                    :href="item.instagram" 
                    target="_blank" 
                    class="tw-p-1 tw-rounded tw-text-gray-400 hover:tw-text-pink-600 hover:tw-bg-pink-50 tw-transition-colors tw-duration-150"
                    :title="`Instagram: ${item.instagram}`"
                >
                    <icon icon="proicons:instagram" class="tw-text-sm" />
                </a>
                <a 
                    v-if="item.linkedin" 
                    :href="item.linkedin" 
                    target="_blank" 
                    class="tw-p-1 tw-rounded tw-text-gray-400 hover:tw-text-blue-700 hover:tw-bg-blue-50 tw-transition-colors tw-duration-150"
                    :title="`LinkedIn: ${item.linkedin}`"
                >
                    <icon icon="ri:linkedin-fill" class="tw-text-sm" />
                </a>
                <a 
                    v-if="item.youtube" 
                    :href="item.youtube" 
                    target="_blank" 
                    class="tw-p-1 tw-rounded tw-text-gray-400 hover:tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors tw-duration-150"
                    :title="`YouTube: ${item.youtube}`"
                >
                    <icon icon="famicons:logo-youtube" class="tw-text-sm" />
                </a>
            </div>
            <span v-else class="tw-text-sm tw-text-gray-500">-</span>
        </td>

        <!-- Status -->
        <td v-if="false" class="tw-px-4 tw-py-3">
            <span :class="getStatusClasses(item.status)" class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium">
                <icon :icon="getStatusIcon(item.status)" class="tw-mr-1 tw-text-xs" />
                {{ getStatusText(item.status) }}
            </span>
        </td>

        <!-- Created At -->
        <td v-if="false" class="tw-px-4 tw-py-3">
            <span class="tw-text-sm tw-text-gray-900">{{ formatDate(item.created_at) }}</span>
        </td>

        <!-- Actions -->
        <td class="tw-px-4 tw-py-3">
            <CompanyTableActions 
                :item="item"
                @edit="$emit('edit', item)"
                @delete="$emit('delete', item)"
                @view="$emit('view', item)"
            />
        </td>
    </tr>
</template>

<script setup>
import { defineProps, defineEmits, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CompanyTableActions from './CompanyTableActions.vue';

const { t } = useI18n();

defineProps({
    item: {
        type: Object,
        required: true
    }
});

defineEmits(['edit', 'delete', 'view']);

// Copy functionality
const copyFeedback = ref({});

const handleCopy = async (text, type) => {
    try {
        await navigator.clipboard.writeText(text);
        copyFeedback.value = { [type]: true };
        setTimeout(() => {
            copyFeedback.value = { [type]: false };
        }, 1000);
    } catch (err) {
        console.error('Failed to copy text: ', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            copyFeedback.value = { [type]: true };
            setTimeout(() => {
                copyFeedback.value = { [type]: false };
            }, 1000);
        } catch (fallbackErr) {
            console.error('Fallback copy failed: ', fallbackErr);
        }
        document.body.removeChild(textArea);
    }
};

// Status options
const statusOptions = [
    { id: 'active', label: t('common.active', 'Active'), icon: 'solar:check-circle-bold-duotone', classes: 'tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100' },
    { id: 'inactive', label: t('common.inactive', 'Inactive'), icon: 'solar:close-circle-bold-duotone', classes: 'tw-bg-red-50 tw-text-red-700 hover:tw-bg-red-100' },
    { id: 'pending', label: t('common.pending', 'Pending'), icon: 'solar:clock-circle-bold-duotone', classes: 'tw-bg-yellow-50 tw-text-yellow-700 hover:tw-bg-yellow-100' }
];

// Helper function to get status display
const getStatusText = (status) => {
    if (!status) return t('common.unknown', 'Unknown');
    const option = statusOptions.find(opt => opt.id === status);
    return option ? option.label : t('common.unknown', 'Unknown');
};

const getStatusIcon = (status) => {
    if (!status) return 'solar:question-circle-bold-duotone';
    const option = statusOptions.find(opt => opt.id === status);
    return option ? option.icon : 'solar:question-circle-bold-duotone';
};

const getStatusClasses = (status) => {
    if (!status) return 'tw-bg-gray-50 tw-text-gray-500';
    const option = statusOptions.find(opt => opt.id === status);
    return option ? option.classes : 'tw-bg-gray-50 tw-text-gray-500';
};

// Helper function to get country flag
const getCountryFlag = (countryName) => {
    // Simple country to flag mapping - you can expand this
    const flagMap = {
        'United States': '🇺🇸',
        'United Kingdom': '🇬🇧',
        'Germany': '🇩🇪',
        'France': '🇫🇷',
        'Canada': '🇨🇦',
        'Australia': '🇦🇺',
        'Japan': '🇯🇵',
        'China': '🇨🇳',
        'India': '🇮🇳',
        'Brazil': '🇧🇷',
        'Mexico': '🇲🇽',
        'Spain': '🇪🇸',
        'Italy': '🇮🇹',
        'Netherlands': '🇳🇱',
        'Sweden': '🇸🇪',
        'Norway': '🇳🇴',
        'Denmark': '🇩🇰',
        'Finland': '🇫🇮',
        'Switzerland': '🇨🇭',
        'Austria': '🇦🇹',
        'Morocco': '🇲🇦'
    };
    return flagMap[countryName] || '🌍';
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Helper function to format website URL
const formatWebsite = (url) => {
    if (!url) return '';
    try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch {
        return url;
    }
};

// Helper function to check if company has social media
const hasSocialMedia = (item) => {
    return item.facebook || item.twitter || item.instagram || item.linkedin || item.youtube;
};

// Helper function to get remaining categories text
const getRemainingCategoriesText = (categories) => {
    return categories.map(cat => cat.name).join(', ');
};

// Helper function to get remaining certificates text
const getRemainingCertificatesText = (certificates) => {
    return certificates.map(cert => cert.name).join(', ');
};
</script>

<style scoped>
/* Add any custom styles here */
</style> 